/**
 * @fileoverview Robust lazy-loading utility with chunk recovery.
 *
 * Handles stale/invalid dynamic import chunks after deployments by:
 * 1. Detecting chunk load failures via error signature.
 * 2. Attempting one controlled hard reload per module per 5-minute window.
 * 3. Throwing a typed error for graceful fallback UI if reload already attempted.
 *
 * IMPORTANT: Never returns a never-resolving promise. All paths either resolve
 * with the module, or throw a ChunkLoadError for the error boundary to catch.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RETRY_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'chunk_recovery_';

function isChunkError(error: unknown): boolean {
  const msg = (() => {
    if (error instanceof Error) return error.message.toLowerCase();
    if (typeof error === 'string') return error.toLowerCase();
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message).toLowerCase();
    }
    return '';
  })();

  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script')
  );
}

/**
 * Get a stable, short key for a module factory function.
 */
function getModuleKey(factory: () => Promise<unknown>): string {
  const str = factory.toString();
  const match = str.match(/import\(["']([^"']+)["']\)/);
  return match?.[1] ?? str.slice(0, 80);
}

function getRetryState(key: string): { timestamp: number; attempts: number } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setRetryState(key: string, state: { timestamp: number; attempts: number }) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(state));
  } catch {
    // storage full or unavailable – ignore
  }
}

function clearRetryState(key: string) {
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // ignore
  }
}

/**
 * Custom error class so error boundaries can detect chunk failures
 * and show the recovery UI instead of a generic error.
 */
export class ChunkLoadError extends Error {
  readonly moduleKey: string;
  readonly attemptCount: number;
  constructor(moduleKey: string, attemptCount: number = 1) {
    super(`Failed to load module: ${moduleKey}`);
    this.name = 'ChunkLoadError';
    this.moduleKey = moduleKey;
    this.attemptCount = attemptCount;
  }
}

/**
 * Drop-in replacement for React.lazy() with automatic chunk recovery.
 *
 * Recovery strategy:
 * 1. First failure → throw ChunkLoadError immediately so boundary UI renders.
 *    Then schedule one hard reload with cache-busting query param.
 * 2. Second failure (within 5 min window) → throw ChunkLoadError directly.
 *
 * This ensures the UI NEVER hangs on a never-resolving promise.
 */
export function lazyWithRecovery<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  const moduleKey = getModuleKey(factory);

  return lazy(async () => {
    const now = Date.now();

    // Clean up stale retry markers
    const existing = getRetryState(moduleKey);
    if (existing && now - existing.timestamp > RETRY_WINDOW_MS) {
      clearRetryState(moduleKey);
    }

    try {
      const mod = await factory();
      // Success – clear any retry state
      clearRetryState(moduleKey);
      return mod;
    } catch (error) {
      if (!isChunkError(error)) {
        throw error; // not a chunk error, let it bubble normally
      }

      const state = getRetryState(moduleKey) || { timestamp: now, attempts: 0 };
      state.attempts += 1;
      state.timestamp = now;
      setRetryState(moduleKey, state);

      // Log for diagnostics
      console.warn(
        `[lazyWithRecovery] Chunk load failed for "${moduleKey}" (attempt ${state.attempts})`,
        { error, route: window.location.pathname },
      );

      if (state.attempts <= 1) {
        // First failure: show fallback immediately, then attempt one hard reload.
        // setTimeout allows React boundary UI to paint before navigation starts.
        setTimeout(() => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('_cb', String(now));
            window.location.replace(url.toString());
          } catch {
            window.location.reload();
          }
        }, 120);
      }

      // Always throw – either reload is happening (and this error is transient),
      // or we already reloaded and still failing. Either way the error boundary
      // will show recovery UI instead of a blank screen.
      throw new ChunkLoadError(moduleKey, state.attempts);
    }
  });
}
